import { Component } from "vue";
import { RouteRecordRaw } from "vue-router";
import BasicLayout from "@/layout/index.vue";
import { RouterTable, IamRouterTable, RouterObj } from "@/types/api/login";
import RouteView from "@/layout/routerView/routerView.vue";
import RouterIcon from "@/router/icon";
import { RouteTemplte } from "@/constant";
import { APP_LIST } from "@/configs/childAppConfig";

export type IamOrTemplate = IamRouterTable[] | RouteTemplte[];

const modules = import.meta.glob("/src/views/**/*.vue");

const IAM_PERMISSION_TYPE = {
  DIR: "DIR",
  TAB: "TAB",
  BTN: "BTN",
  ATTR: "ATTR",
};

export const isIndependentPage = (name: string) => {
  const pattern = /^[A-Z_]+$/;
  return pattern.test(name);
};

export const recursionMerge = (
  base: IamOrTemplate,
  source: IamRouterTable[]
) => {
  const baseRoutePaths = base.map((b) => b.path); // 主应用route path list
  source.forEach((routeItem) => {
    if (baseRoutePaths.includes(routeItem.path)) {
      // 主应用中包含了子应用某个路由，需要合并
      const currentBaseIndex = base.findIndex((b) => b.path === routeItem.path);
      const currentBase = base[currentBaseIndex];
      if (routeItem.children && routeItem.children.length) {
        recursionMerge(
          currentBase?.children as IamOrTemplate,
          routeItem.children
        );
      } else if (
        currentBaseIndex > -1 &&
        routeItem.funcType === IAM_PERMISSION_TYPE.TAB
      ) {
        // 相同path的路由，子应用覆盖掉主应用
        base[currentBaseIndex] = routeItem;
      }
    } else {
      // 主应用中不包含了子应用某个路由，需要添加
      base.push(routeItem);
    }
  });
};
const recursiveSort = (routes: any[]) => {
  return routes
    .sort((a: { funcSort: number }, b: { funcSort: number }) => a.funcSort - b.funcSort)
    .map((route) => {
      if (route.children && route.children.length) {
        route.children = recursiveSort(route.children); // 递归对子路由排序
      }
      return route;
    });
};
export const serializRouter: (
  T: IamOrTemplate,
  S: Array<IamRouterTable[]>
) => RouterObj[] = (target, sources) => {
  // 1. 递归合并所有子应用路由到主应用
  sources.forEach((s) => recursionMerge(target, s));

  // // 2. 路由递归排序(2级，TODO: 实现递归排序所有子路由)
  // target = target.sort(
  //   (a: { funcSort: number }, b: { funcSort: number }) =>
  //     a.funcSort - b.funcSort
  // );
  // target.forEach((r) => {
  //   if (r.children && r.children.length) {
  //     r.children = r.children.sort((ra, rb) => ra.funcSort - rb.funcSort);
  //   }
  // });
  // 4. 构建并返回包含root的router tree
  target = recursiveSort(target);
  return makeRootRoute(transformIamRouter(target as IamRouterTable[]));
};

export const labelForChildApp: <T extends any[]>(
  R: T,
  C: string
) => IamOrTemplate = (routes, code) => {
  return routes.map((r) => {
    let appName = code;
    if (r.query && typeof r.query == "string" && r.query.length) {
      try {
        appName = JSON.parse(r.query).sysCode;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("该目录没有系统code");
      }
    }
    return {
      ...r,
      appName: appName,
      children:
        r.children && r.children.length
          ? labelForChildApp(r.children, code)
          : [],
    };
  });
};

/**
 * @desc: 解析路由
 * @param { Array } routerMap 后台返回的路由表
 * @param { Object } 可选 路由对象
 * @return 返回RouteRecordRaw
 */
export const generator = (routerMap: RouterTable, parent?: RouteRecordRaw) => {
  type basic = {
    BasicLayout: Component;
    RouteView: Component;
  } & Record<string, any>;

  interface ptachCurrent {
    children?: RouteRecordRaw[] | undefined;
    redirect?: string | undefined;
  }
  const constantRouterComponents: basic = { BasicLayout, RouteView };
  return routerMap.map((item) => {
    let query = {};
    try {
      query = JSON.parse(item.query || "{}");
    } catch {
      // do nothing
    }
    const currentRouter: RouteRecordRaw = {
      path: item.path || `${(parent && parent.path) || ""}/${item.key}`,
      name: item.key || "",
      component:
        constantRouterComponents[item.component ?? item.key ?? ""] ||
        modules[`/src/views${item.component}.vue`],
      meta: {
        id: item.id,
        title: item.name,
        keepAlive: item.keepAlive || false,
        hidden: item.hidden || false,
        permissions: item.permissions,
        params: item.path
          ? item.path.indexOf("/:") >= 0
            ? item.path.split("/:")[1]
            : ""
          : null,
        ...query,
      },
    };
    if (
      typeof currentRouter.name === "string" &&
      currentRouter.name.indexOf("/:") >= 0
    ) {
      currentRouter.name = currentRouter.name.split("/:")[0];
    }
    const ptachCurrent: ptachCurrent = {};
    // 为了防止出现后端返回结果不规范，处理有可能出现拼接出两个 反斜杠
    if (!currentRouter.path.startsWith("http")) {
      currentRouter.path = currentRouter.path.replace("//", "/");
    }

    // 目录路由默认重定向到第一个子菜单
    if (item.funcType === IAM_PERMISSION_TYPE.DIR) {
      if (item.children && item.children.length) {
        item.redirect = item.children[0].path || "/404";
      }
    }

    // 绑定重定向
    item.redirect && (ptachCurrent.redirect = item.redirect);

    // 是否有子菜单，并递归处理
    if (item.children && item.children.length > 0) {
      ptachCurrent.children = generator(item.children, currentRouter);
    }

    return Object.assign(currentRouter, ptachCurrent);
  });
};

export const transformIamRouter = (
  iamRouter: IamRouterTable[]
): RouterTable => {
  return iamRouter.map((r) => {
    if (r.children && r.children.length) {
      // 将按钮和属性权限提取出来
      const permissions = [];
      for (let i = 0; i < r.children.length; i++) {
        const item = r.children[i];
        if (
          [IAM_PERMISSION_TYPE.BTN, IAM_PERMISSION_TYPE.ATTR].includes(
            item.funcType
          )
        ) {
          permissions.push(item.component);
          r.children.splice(i, 1);
          i--;
        }
      }
      if (permissions.length) {
        r.permissions = permissions;
      }
    }
    // let appName = ""
    // if(r.query && typeof r.query == "string" && r.query.length){
    //   appName = r.query
    // }
    return {
      appName: r.appName,
      id: r.funcId,
      path: r.path,
      name: r.funcName,
      component: r.component,
      funcType:r.funcType,
      key: r.path,
      icon: RouterIcon[r.path],
      children:
        r.children && r.children.length ? transformIamRouter(r.children) : [],
      pid: r.portalId,
      hidden: !r.isShow || isIndependentPage(r.funcName),
      keepAlive: true,
      permissions: r.permissions || [],
      query: r.query,
    };
  });
};

/**
 *
 * @param {Array} data 用户中心返回的路由表
 * @returns 根路由，不要修改其数据结构
 */

export const makeRootRoute = (data: RouterTable): any[] => {
  const isExpandKeys: Array<string> = [];
  const newData: any = [];
  for (const item of APP_LIST) {
    if (item.isExpand) {
      isExpandKeys.push(item.sysCode);
    } else {
      newData.push({
        appName: item.sysCode,
        id: item.sysCode,
        path: "",
        name: item.sysName,
        component: "RouteView",
        key: item.sysCode,
        children: [],
        pid: item.sysCode,
        hidden: false,
        keepAlive: true,
        permissions: [],
        query: null,
      });
    }
  }
  for (const vo of data) {
    if (isExpandKeys.includes(vo.appName)) {
      newData.push(vo);
    } else {
      if (vo.appName === import.meta.env.VITE_SYSNAME) {
        newData.push(vo);
        continue;
      }
      const index = newData.findIndex(
        (item: any) => item.appName === vo.appName
      );
      if (index >= 0) {
        newData[index].children.push(vo);
      }
    }
  }
  // 3. 添加子应用 wujie container
  const menuList = APP_LIST.map((v) => {
    return {
      funcName: v.sysName,
      path: v.path,
      status: true,
      funcType: "DIR",
      children: [],
      hasChildren: false,
      funcSort: 0,
      isShow: 0,
      hidden:true,
      component: v.component,
      icon: "#",
      query: "", // 添加默认页面
    };
  });
  newData.push(...menuList);
  return [
    {
      id: 1,
      pid: 0,
      path: "/",
      name: "",
      key: "layout",
      component: "BasicLayout",
      children: newData,
    },
  ];
};
